import React from 'react';
import dayjs from 'dayjs';
import { Table } from 'antd';
import { ClipboardCheck, Calendar, Clock, Store, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { tone, StatusTag } from './page';

const initials = (name = '') =>
    name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join('') || '?';

export default function RecentAudits({ storeChecks, loading }) {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = React.useState(false);

    const StoreTypeColumns = [
        {
            title: 'Auditoría',
            dataIndex: 'createdAt',
            key: 'createdAt',
            defaultSortOrder: 'descend',
            sorter: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
            render: (text, record) => {
                const date = dayjs(text).format('DD/MM/YYYY');
                return (
                    <button
                        type="button"
                        onClick={() => navigate(`/dashboard/storeCheck?id=${record._id}`)}
                        className="inline-flex items-center gap-2 p-0 bg-transparent border-0 cursor-pointer text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline underline-offset-2 tabular-nums transition-colors">
                        <Calendar className="text-[14px] text-surface-400" />
                        {date}
                    </button>
                );
            }
        },
        {
            title: 'Productos',
            dataIndex: 'products',
            key: 'products',
            align: 'right',
            render: (text) => {
                const length = text?.length || 0;
                return <span className="text-sm text-surface-700 tabular-nums">{length} productos</span>;
            }
        },
        {
            title: 'Piso de Venta',
            dataIndex: 'storeName',
            key: 'storeName',
            render: (text) => (
                <span className="flex items-center gap-2 min-w-0">
                    <Store className="text-[14px] text-surface-400" />
                    <span className="text-sm text-surface-900 truncate">{text}</span>
                </span>
            )
        },
        {
            title: 'Auditor',
            dataIndex: 'userName',
            key: 'userName',
            render: (text) => (
                <span className="flex items-center gap-2 min-w-0">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-100 text-[10px] font-semibold text-surface-600">
                        {initials(text)}
                    </span>
                    <span className="text-sm text-surface-700 truncate">{text}</span>
                </span>
            )
        },
        {
            title: 'Tiempo',
            dataIndex: 'totalTime',
            key: 'totalTime',
            align: 'right',
            render: (text) => {
                const minutes = text || 0;
                return (
                    <StatusTag tone={minutes > 30 ? 'warning' : 'success'} icon={<Clock />} className="tabular-nums">
                        {minutes} min
                    </StatusTag>
                );
            }
        }
    ];

    return (
        <section className="shelf-card overflow-hidden">
            {/* Header */}
            <button
                type="button"
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left bg-transparent border-0 cursor-pointer transition-colors hover:bg-surface-25">
                <span className="flex items-center gap-3 min-w-0">
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[15px] ${tone('info').chip}`}>
                        <ClipboardCheck />
                    </span>
                    <span className="text-[15px] font-semibold text-surface-900 truncate">Auditorías Recientes</span>
                    {storeChecks?.length > 0 && <StatusTag className="tabular-nums">{storeChecks.length} auditorías</StatusTag>}
                </span>
                <ChevronDown className={`text-base text-surface-400 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`} />
            </button>

            {/* Content */}
            {isExpanded && (
                <div className="border-t border-surface-100 overflow-x-auto">
                    <Table
                        size="middle"
                        loading={loading}
                        dataSource={storeChecks || []}
                        columns={StoreTypeColumns}
                        pagination={{
                            pageSize: 10,
                            showTotal: (total) => `Total ${total} auditorías`,
                            className: 'px-5'
                        }}
                        rowKey="_id"
                    />
                </div>
            )}
        </section>
    );
}
